import { buildMixSlug, createFallbackStickerSizeOption } from "@/domain/size-contract";
import type { MixSheetRecipe, MixSheetRecipeItem, SizeId, SizeOption } from "@/domain/types";

export type { MixSheetRecipe, MixSheetRecipeItem } from "@/domain/types";
import { getShopifyConfig } from "./config";
import { shopifyStorefrontRequest } from "./storefront";

export interface ShopifyProductOption {
  name: string;
  values: string[];
}

export interface ShopifySelectedOption {
  name: string;
  value: string;
}

export interface ShopifyMoney {
  amount: string;
  currencyCode: string;
}

interface ShopifyMetafield {
  type: string;
  value: string;
}

export type ShopifyVariantDimensionParseResult =
  | {
      ok: true;
      millimeters: number;
      rawType: string;
      rawValue: string;
    }
  | {
      ok: false;
      reason: "missing" | "invalid";
      message: string;
      rawType: string | null;
      rawValue: string | null;
    };

export type ShopifyLocalizedLabelParseResult =
  | {
      ok: true;
      value: string;
      rawType: string;
      rawValue: string;
    }
  | {
      ok: false;
      reason: "missing" | "invalid";
      message: string;
      rawType: string | null;
      rawValue: string | null;
    };

export interface ShopifyDesignConstraints {
  maxLines: 1 | 2 | 3;
  allowIcon: boolean;
}

/**
 * Expected Shopify metafield definitions on each storefront variant for the
 * sticker product. Keep this block in sync with the store-side setup; the
 * parser below fails explicitly when a variant does not satisfy it.
 *
 * - `custom.label_width_mm`  (single-kind only)
 *     type: number_integer or dimension (mm)
 *     example: 36
 * - `custom.label_height_mm` (single-kind only)
 *     type: number_integer or dimension (mm)
 *     example: 10
 * - `custom.size_label_sv`   (both kinds)
 *     type: single_line_text_field
 *     example: "Liten"
 * - `custom.size_label_en`   (both kinds)
 *     type: single_line_text_field
 *     example: "Small"
 * - `custom.best_for_sv`     (optional, both kinds; absent => message fallback)
 *     type: single_line_text_field
 *     example: "Passar bäst på kläder, flaskor och lådor"
 * - `custom.best_for_en`     (optional, both kinds; absent => message fallback)
 *     type: single_line_text_field
 *     example: "Best for clothes, bottles and boxes"
 * - `custom.variant_kind`    (optional; absent => treated as "single")
 *     type: single_line_text_field
 *     allowed values: "single" | "mix"
 * - `custom.mix_sheet_recipe` (required when variant_kind = "mix")
 *     type: json
 *     example: {"items":[{"sizeId":"small","count":4},{"sizeId":"medium","count":2},{"sizeId":"large","count":1}]}
 * - `custom.design_constraints` (required for single-kind variants; ignored for mix)
 *     type: json
 *     example: {"maxLines":1,"allowIcon":false}
 */

export type ShopifyProductVariantKind = "single" | "mix";

interface ShopifyProductVariantBase {
  id: string;
  title: string;
  availableForSale: boolean;
  selectedOptions: ShopifySelectedOption[];
  price: ShopifyMoney;
  /** Pre-sale price. `null` unless the variant is on sale in Shopify. */
  compareAtPrice: ShopifyMoney | null;
  sizeLabelSv: ShopifyLocalizedLabelParseResult;
  sizeLabelEn: ShopifyLocalizedLabelParseResult;
  /** "What it fits best" copy from `custom.best_for_sv`; absent when unset. */
  bestForSv?: string;
  /** "What it fits best" copy from `custom.best_for_en`; absent when unset. */
  bestForEn?: string;
}

export type ShopifyProductVariant =
  | (ShopifyProductVariantBase & {
      kind: "single";
      labelWidthMm: ShopifyVariantDimensionParseResult;
      labelHeightMm: ShopifyVariantDimensionParseResult;
      designConstraints: ShopifyDesignConstraints;
    })
  | (ShopifyProductVariantBase & {
      kind: "mix";
      recipe: MixSheetRecipe;
    });

export interface ShopifyProduct {
  id: string;
  handle: string;
  title: string;
  options: ShopifyProductOption[];
  variants: {
    nodes: ShopifyProductVariant[];
  };
}

export interface ShopifyStickerSizeCatalogEntry {
  sizeId: SizeId;
  optionValue: string;
  variantId: string;
  price: ShopifyMoney;
  unitPrice: number;
  /** Pre-sale price in minor units, or `null` when the variant isn't on sale. */
  compareAtUnitPrice: number | null;
  currency: string;
  widthMm: number;
  heightMm: number;
  displayLabelSv: string;
  displayLabelEn: string;
  /** "What it fits best" copy; absent when the metafield is unset. */
  bestForSv?: string;
  bestForEn?: string;
  constraints: ShopifyDesignConstraints;
}

export interface ShopifyStickerMixCatalogEntry {
  variantId: string;
  optionValue: string;
  price: ShopifyMoney;
  unitPrice: number;
  /** Pre-sale price in minor units, or `null` when the variant isn't on sale. */
  compareAtUnitPrice: number | null;
  currency: string;
  recipe: MixSheetRecipe;
  displayLabelSv: string;
  displayLabelEn: string;
  /** "What it fits best" copy; absent when the metafield is unset. */
  bestForSv?: string;
  bestForEn?: string;
  /**
   * Stable recipe-derived slug used by shareable URL state. Pure function of
   * `recipe` — two variants with the same recipe (regardless of item order)
   * produce the same slug; the catalog loader fails loud on collisions.
   */
  slug: string;
}

export interface ShopifyStickerCatalog {
  singleEntries: ShopifyStickerSizeCatalogEntry[];
  singleBySizeId: Record<string, ShopifyStickerSizeCatalogEntry>;
  mixEntries: ShopifyStickerMixCatalogEntry[];
}

export function getSingleEntries(
  catalog: ShopifyStickerCatalog,
): ShopifyStickerSizeCatalogEntry[] {
  return catalog.singleEntries;
}

export function getMixEntries(
  catalog: ShopifyStickerCatalog,
): ShopifyStickerMixCatalogEntry[] {
  return catalog.mixEntries;
}

export function getSingleEntryBySizeId(
  catalog: ShopifyStickerCatalog,
  sizeId: SizeId,
): ShopifyStickerSizeCatalogEntry | undefined {
  return catalog.singleBySizeId[sizeId];
}

export function getSingleEntryConstraints(
  catalog: ShopifyStickerCatalog,
  sizeId: SizeId,
): ShopifyDesignConstraints | undefined {
  return catalog.singleBySizeId[sizeId]?.constraints;
}

export type ShopifyStickerSizeDimensions = SizeOption["dimensions"];

interface ShopifyProductByHandleResponse {
  product: ShopifyProductResponse | null;
}

interface ShopifyProductVariantResponse {
  id: string;
  title: string;
  availableForSale: boolean;
  selectedOptions: ShopifySelectedOption[];
  price: ShopifyMoney | null;
  compareAtPrice: ShopifyMoney | null;
  labelWidthMetafield: ShopifyMetafield | null;
  labelHeightMetafield: ShopifyMetafield | null;
  sizeLabelSvMetafield: ShopifyMetafield | null;
  sizeLabelEnMetafield: ShopifyMetafield | null;
  bestForSvMetafield?: ShopifyMetafield | null;
  bestForEnMetafield?: ShopifyMetafield | null;
  variantKindMetafield: ShopifyMetafield | null;
  mixSheetRecipeMetafield: ShopifyMetafield | null;
  designConstraintsMetafield: ShopifyMetafield | null;
}

interface ShopifyProductResponse {
  id: string;
  handle: string;
  title: string;
  options: ShopifyProductOption[];
  variants: {
    nodes: ShopifyProductVariantResponse[];
  };
}

export class ShopifyProductNotFoundError extends Error {
  constructor(handle: string) {
    super(`Configured Shopify product was not found for handle "${handle}".`);
    this.name = "ShopifyProductNotFoundError";
  }
}

export class ShopifyProductContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ShopifyProductContractError";
  }
}

export function shopifyStickerSizeCatalogEntryToDimensions(
  entry: Pick<ShopifyStickerSizeCatalogEntry, "widthMm" | "heightMm">,
): ShopifyStickerSizeDimensions {
  return {
    widthMm: entry.widthMm,
    heightMm: entry.heightMm,
  };
}

export function createShopifyBackedSizeOption(
  entry: Pick<ShopifyStickerSizeCatalogEntry, "sizeId" | "widthMm" | "heightMm">,
): SizeOption {
  const size = createFallbackStickerSizeOption(entry.sizeId);

  if (!size) {
    throw new ShopifyProductContractError(
      `Sticker size "${entry.sizeId}" is not part of the app size contract.`,
    );
  }

  return {
    ...size,
    dimensions: shopifyStickerSizeCatalogEntryToDimensions(entry),
  };
}

const STICKER_PRODUCT_QUERY = `
  query GetStickerProductByHandle($handle: String!) {
    product(handle: $handle) {
      id
      handle
      title
      options {
        name
        values
      }
      variants(first: 50) {
        nodes {
          id
          title
          availableForSale
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          labelWidthMetafield: metafield(namespace: "custom", key: "label_width_mm") {
            type
            value
          }
          labelHeightMetafield: metafield(namespace: "custom", key: "label_height_mm") {
            type
            value
          }
          sizeLabelSvMetafield: metafield(namespace: "custom", key: "size_label_sv") {
            type
            value
          }
          sizeLabelEnMetafield: metafield(namespace: "custom", key: "size_label_en") {
            type
            value
          }
          bestForSvMetafield: metafield(namespace: "custom", key: "best_for_sv") {
            type
            value
          }
          bestForEnMetafield: metafield(namespace: "custom", key: "best_for_en") {
            type
            value
          }
          variantKindMetafield: metafield(namespace: "custom", key: "variant_kind") {
            type
            value
          }
          mixSheetRecipeMetafield: metafield(namespace: "custom", key: "mix_sheet_recipe") {
            type
            value
          }
          designConstraintsMetafield: metafield(namespace: "custom", key: "design_constraints") {
            type
            value
          }
          selectedOptions {
            name
            value
          }
        }
      }
    }
  }
`;

const DIMENSION_MM_UNITS = new Set(["mm", "millimeter", "millimeters", "millimetre", "millimetres"]);

function parsePositiveNumber(value: unknown): number | null {
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value.trim())
        : Number.NaN;

  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
}

function normalizeDimensionUnit(value: unknown): string | null {
  return typeof value === "string" ? value.trim().toLowerCase() : null;
}

function tryParseMillimetersFromDimensionPayload(value: string): number | null {
  let parsedValue: unknown;

  try {
    parsedValue = JSON.parse(value);
  } catch {
    return null;
  }

  const directNumber = parsePositiveNumber(parsedValue);
  if (directNumber !== null) {
    return directNumber;
  }

  if (!parsedValue || typeof parsedValue !== "object") {
    return null;
  }

  const dimensionRecord = parsedValue as Record<string, unknown>;
  const unit = normalizeDimensionUnit(dimensionRecord.unit);

  if (unit && !DIMENSION_MM_UNITS.has(unit)) {
    return null;
  }

  return (
    parsePositiveNumber(dimensionRecord.value) ??
    parsePositiveNumber(dimensionRecord.amount)
  );
}

function parseVariantDimensionMetafield(
  metafield: ShopifyMetafield | null,
  variantTitle: string,
  fieldLabel: string,
): ShopifyVariantDimensionParseResult {
  if (!metafield) {
    return {
      ok: false,
      reason: "missing",
      message: `Variant "${variantTitle}" is missing metafield "${fieldLabel}".`,
      rawType: null,
      rawValue: null,
    };
  }

  const rawValue = metafield.value.trim();
  const parsedMillimeters =
    parsePositiveNumber(rawValue) ?? tryParseMillimetersFromDimensionPayload(rawValue);

  if (parsedMillimeters !== null) {
    return {
      ok: true,
      millimeters: parsedMillimeters,
      rawType: metafield.type,
      rawValue: metafield.value,
    };
  }

  return {
    ok: false,
    reason: "invalid",
    message: `Variant "${variantTitle}" metafield "${fieldLabel}" could not be parsed as a positive millimeter value.`,
    rawType: metafield.type,
    rawValue: metafield.value,
  };
}

function parseVariantLabelMetafield(
  metafield: ShopifyMetafield | null,
  variantTitle: string,
  fieldLabel: string,
): ShopifyLocalizedLabelParseResult {
  if (!metafield) {
    return {
      ok: false,
      reason: "missing",
      message: `Variant "${variantTitle}" is missing metafield "${fieldLabel}".`,
      rawType: null,
      rawValue: null,
    };
  }

  const normalizedValue = metafield.value.trim();

  if (normalizedValue) {
    return {
      ok: true,
      value: normalizedValue,
      rawType: metafield.type,
      rawValue: metafield.value,
    };
  }

  return {
    ok: false,
    reason: "invalid",
    message: `Variant "${variantTitle}" metafield "${fieldLabel}" must be a non-empty string.`,
    rawType: metafield.type,
    rawValue: metafield.value,
  };
}

/**
 * Optional free-text metafield: a missing, empty or whitespace-only value is
 * "absent" (`undefined`) so callers fall back to message copy instead of failing
 * the whole catalog on marketing copy.
 */
function parseOptionalVariantTextMetafield(
  metafield: ShopifyMetafield | null | undefined,
): string | undefined {
  const value = metafield?.value?.trim();
  return value ? value : undefined;
}

const VARIANT_KIND_VALUES = new Set<ShopifyProductVariantKind>(["single", "mix"]);

function parseVariantKindMetafield(
  metafield: ShopifyMetafield | null,
  variantTitle: string,
): ShopifyProductVariantKind {
  if (!metafield) {
    return "single";
  }

  const rawValue = metafield.value?.trim() ?? "";

  if (!rawValue) {
    return "single";
  }

  if ((VARIANT_KIND_VALUES as Set<string>).has(rawValue)) {
    return rawValue as ShopifyProductVariantKind;
  }

  throw new ShopifyProductContractError(
    `Variant "${variantTitle}" metafield "custom.variant_kind" has unknown value "${metafield.value}"; expected "single" or "mix".`,
  );
}

function parseMixSheetRecipeMetafield(
  metafield: ShopifyMetafield | null,
  variantTitle: string,
  stickerSizeOptionValues: Record<SizeId, string>,
): MixSheetRecipe {
  if (!metafield) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" is missing required metafield "custom.mix_sheet_recipe" for a mix-kind variant.`,
    );
  }

  const rawValue = metafield.value?.trim() ?? "";

  if (!rawValue) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" is empty.`,
    );
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(rawValue);
  } catch {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" is not valid JSON.`,
    );
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" must be a JSON object with an "items" array.`,
    );
  }

  const itemsValue = (parsed as { items?: unknown }).items;

  if (!Array.isArray(itemsValue)) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" is missing an "items" array.`,
    );
  }

  if (itemsValue.length === 0) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" must contain at least one item.`,
    );
  }

  const validSizeIds = new Set<string>(Object.keys(stickerSizeOptionValues));
  const seenSizeIds = new Set<string>();
  const items: MixSheetRecipeItem[] = [];

  for (const [index, rawItem] of itemsValue.entries()) {
    if (!rawItem || typeof rawItem !== "object") {
      throw new ShopifyProductContractError(
        `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" item at index ${index} is not an object.`,
      );
    }

    const { sizeId: rawSizeId, count: rawCount } = rawItem as {
      sizeId?: unknown;
      count?: unknown;
    };

    if (typeof rawSizeId !== "string" || !rawSizeId) {
      throw new ShopifyProductContractError(
        `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" item at index ${index} has an invalid "sizeId".`,
      );
    }

    if (!validSizeIds.has(rawSizeId)) {
      throw new ShopifyProductContractError(
        `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" references unknown sizeId "${rawSizeId}" at index ${index}.`,
      );
    }

    if (
      typeof rawCount !== "number" ||
      !Number.isInteger(rawCount) ||
      rawCount < 1
    ) {
      throw new ShopifyProductContractError(
        `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" item at index ${index} must have an integer "count" >= 1.`,
      );
    }

    if (seenSizeIds.has(rawSizeId)) {
      throw new ShopifyProductContractError(
        `Variant "${variantTitle}" metafield "custom.mix_sheet_recipe" has duplicate sizeId "${rawSizeId}" at index ${index}.`,
      );
    }

    seenSizeIds.add(rawSizeId);
    items.push({ sizeId: rawSizeId as SizeId, count: rawCount });
  }

  return { items };
}

const DESIGN_CONSTRAINT_KEYS = new Set<string>(["maxLines", "allowIcon"]);

// Single-kind variants must carry design_constraints. Mix-kind variants do not;
// the recipe's constituent sizes each carry their own constraints on their own
// single-kind variants, so the call site in normalizeStickerProductVariant
// decides whether to invoke this parser.
function parseDesignConstraintsMetafield(
  metafield: ShopifyMetafield | null,
  variantTitle: string,
): ShopifyDesignConstraints {
  if (!metafield) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" is missing required metafield "custom.design_constraints".`,
    );
  }

  const rawValue = metafield.value?.trim() ?? "";

  if (!rawValue) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.design_constraints" is empty.`,
    );
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(rawValue);
  } catch {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.design_constraints" is not valid JSON.`,
    );
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.design_constraints" must be a JSON object with keys "maxLines" and "allowIcon".`,
    );
  }

  const entries = parsed as Record<string, unknown>;

  for (const key of Object.keys(entries)) {
    if (!DESIGN_CONSTRAINT_KEYS.has(key)) {
      throw new ShopifyProductContractError(
        `Variant "${variantTitle}" metafield "custom.design_constraints" has unknown key "${key}".`,
      );
    }
  }

  if (!("maxLines" in entries)) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.design_constraints" is missing "maxLines".`,
    );
  }

  if (!("allowIcon" in entries)) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.design_constraints" is missing "allowIcon".`,
    );
  }

  const { maxLines: rawMaxLines, allowIcon: rawAllowIcon } = entries;

  if (
    typeof rawMaxLines !== "number" ||
    !Number.isInteger(rawMaxLines) ||
    rawMaxLines < 1 ||
    rawMaxLines > 3
  ) {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.design_constraints" has invalid "maxLines"; expected an integer in [1, 3].`,
    );
  }

  if (typeof rawAllowIcon !== "boolean") {
    throw new ShopifyProductContractError(
      `Variant "${variantTitle}" metafield "custom.design_constraints" has invalid "allowIcon"; expected a boolean.`,
    );
  }

  return {
    maxLines: rawMaxLines as 1 | 2 | 3,
    allowIcon: rawAllowIcon,
  };
}

function normalizeVariantPrice(
  variant: ShopifyProductVariantResponse,
  productHandle: string,
): ShopifyMoney {
  const amount = variant.price?.amount?.trim();
  const currencyCode = variant.price?.currencyCode?.trim();

  if (!amount || !currencyCode) {
    throw new ShopifyProductContractError(
      `Shopify product "${productHandle}" variant "${variant.title}" is missing price data.`,
    );
  }

  return {
    amount,
    currencyCode,
  };
}

/**
 * Compare-at is optional merchandising data, not part of the product contract:
 * Shopify omits it whenever the variant isn't on sale. A partially-filled
 * money object is still a contract error — that would mean a malformed
 * response rather than "no sale".
 */
function normalizeVariantCompareAtPrice(
  variant: ShopifyProductVariantResponse,
  productHandle: string,
): ShopifyMoney | null {
  if (!variant.compareAtPrice) {
    return null;
  }

  const amount = variant.compareAtPrice.amount?.trim();
  const currencyCode = variant.compareAtPrice.currencyCode?.trim();

  if (!amount || !currencyCode) {
    throw new ShopifyProductContractError(
      `Shopify product "${productHandle}" variant "${variant.title}" has incomplete compare-at price data.`,
    );
  }

  return {
    amount,
    currencyCode,
  };
}

export function normalizeMoneyAmountToMinorUnits(
  money: ShopifyMoney,
  productHandle: string,
  variantTitle: string,
): number {
  const normalizedAmount = money.amount.trim();
  const match = normalizedAmount.match(/^(\d+)(?:\.(\d{1,2}))?$/);

  if (!match) {
    throw new ShopifyProductContractError(
      `Shopify product "${productHandle}" variant "${variantTitle}" has an invalid price amount "${money.amount}".`,
    );
  }

  const [, wholeUnits, fractionalUnits = ""] = match;
  const minorUnits = Number(wholeUnits) * 100 + Number(fractionalUnits.padEnd(2, "0"));

  if (!Number.isSafeInteger(minorUnits) || minorUnits <= 0) {
    throw new ShopifyProductContractError(
      `Shopify product "${productHandle}" variant "${variantTitle}" has a non-positive price amount "${money.amount}".`,
    );
  }

  return minorUnits;
}

function normalizeStickerProductVariant(
  variant: ShopifyProductVariantResponse,
  productHandle: string,
  stickerSizeOptionValues: Record<SizeId, string>,
): ShopifyProductVariant {
  const base: ShopifyProductVariantBase = {
    id: variant.id,
    title: variant.title,
    availableForSale: variant.availableForSale,
    selectedOptions: variant.selectedOptions,
    price: normalizeVariantPrice(variant, productHandle),
    compareAtPrice: normalizeVariantCompareAtPrice(variant, productHandle),
    sizeLabelSv: parseVariantLabelMetafield(
      variant.sizeLabelSvMetafield,
      variant.title,
      "custom.size_label_sv",
    ),
    sizeLabelEn: parseVariantLabelMetafield(
      variant.sizeLabelEnMetafield,
      variant.title,
      "custom.size_label_en",
    ),
    bestForSv: parseOptionalVariantTextMetafield(variant.bestForSvMetafield),
    bestForEn: parseOptionalVariantTextMetafield(variant.bestForEnMetafield),
  };

  const kind = parseVariantKindMetafield(variant.variantKindMetafield, variant.title);

  if (kind === "mix") {
    return {
      ...base,
      kind: "mix",
      recipe: parseMixSheetRecipeMetafield(
        variant.mixSheetRecipeMetafield,
        variant.title,
        stickerSizeOptionValues,
      ),
    };
  }

  return {
    ...base,
    kind: "single",
    labelWidthMm: parseVariantDimensionMetafield(
      variant.labelWidthMetafield,
      variant.title,
      "custom.label_width_mm",
    ),
    labelHeightMm: parseVariantDimensionMetafield(
      variant.labelHeightMetafield,
      variant.title,
      "custom.label_height_mm",
    ),
    designConstraints: parseDesignConstraintsMetafield(
      variant.designConstraintsMetafield,
      variant.title,
    ),
  };
}

function normalizeStickerProduct(product: ShopifyProductResponse): ShopifyProduct {
  const config = getShopifyConfig();

  return {
    id: product.id,
    handle: product.handle,
    title: product.title,
    options: product.options,
    variants: {
      nodes: product.variants.nodes.map((variant) =>
        normalizeStickerProductVariant(
          variant,
          product.handle,
          config.stickerSizeOptionValues,
        ),
      ),
    },
  };
}

function getVariantSelectedSizeOptionValue(variant: ShopifyProductVariant): string | null {
  const config = getShopifyConfig();
  const sizeOption = variant.selectedOptions.find(
    (selectedOption) => selectedOption.name === config.stickerSizeOptionName,
  );

  return sizeOption?.value ?? null;
}

function getVariantDimensionMillimeters(
  dimension: ShopifyVariantDimensionParseResult,
  productHandle: string,
  variantTitle: string,
  fieldLabel: string,
): number {
  if (dimension.ok) {
    return dimension.millimeters;
  }

  throw new ShopifyProductContractError(
    `Shopify product "${productHandle}" variant "${variantTitle}" has invalid ${fieldLabel}: ${dimension.message}`,
  );
}

function getVariantLocalizedLabel(
  label: ShopifyLocalizedLabelParseResult,
  productHandle: string,
  variantTitle: string,
  fieldLabel: string,
): string {
  if (label.ok) {
    return label.value;
  }

  throw new ShopifyProductContractError(
    `Shopify product "${productHandle}" variant "${variantTitle}" has invalid ${fieldLabel}: ${label.message}`,
  );
}

function getStickerSizeIdByOptionValue(): Map<string, SizeId> {
  const config = getShopifyConfig();

  return new Map<string, SizeId>(
    Object.entries(config.stickerSizeOptionValues).map(([sizeId, optionValue]) => [
      optionValue,
      sizeId,
    ]),
  );
}

function getVariantCompareAtUnitPrice(
  variant: ShopifyProductVariant,
  productHandle: string,
): number | null {
  if (!variant.compareAtPrice) {
    return null;
  }

  return normalizeMoneyAmountToMinorUnits(
    variant.compareAtPrice,
    productHandle,
    variant.title,
  );
}

function createStickerSizeCatalogEntry(
  product: ShopifyProduct,
  variant: ShopifyProductVariant,
  sizeId: SizeId,
  optionValue: string,
): ShopifyStickerSizeCatalogEntry {
  if (variant.kind !== "single") {
    throw new ShopifyProductContractError(
      `Shopify product "${product.handle}" variant "${variant.title}" mapped to size "${sizeId}" is not a single-kind variant; mix-kind variants are not part of the size catalog.`,
    );
  }

  const unitPrice = normalizeMoneyAmountToMinorUnits(variant.price, product.handle, variant.title);

  return {
    sizeId,
    optionValue,
    variantId: variant.id,
    price: variant.price,
    unitPrice,
    compareAtUnitPrice: getVariantCompareAtUnitPrice(variant, product.handle),
    currency: variant.price.currencyCode,
    widthMm: getVariantDimensionMillimeters(
      variant.labelWidthMm,
      product.handle,
      variant.title,
      "custom.label_width_mm",
    ),
    heightMm: getVariantDimensionMillimeters(
      variant.labelHeightMm,
      product.handle,
      variant.title,
      "custom.label_height_mm",
    ),
    displayLabelSv: getVariantLocalizedLabel(
      variant.sizeLabelSv,
      product.handle,
      variant.title,
      "custom.size_label_sv",
    ),
    displayLabelEn: getVariantLocalizedLabel(
      variant.sizeLabelEn,
      product.handle,
      variant.title,
      "custom.size_label_en",
    ),
    bestForSv: variant.bestForSv,
    bestForEn: variant.bestForEn,
    constraints: variant.designConstraints,
  };
}

function createStickerMixCatalogEntry(
  product: ShopifyProduct,
  variant: ShopifyProductVariant,
  optionValue: string,
): ShopifyStickerMixCatalogEntry {
  if (variant.kind !== "mix") {
    throw new ShopifyProductContractError(
      `Shopify product "${product.handle}" variant "${variant.title}" is not a mix-kind variant; expected a mix variant here.`,
    );
  }

  const unitPrice = normalizeMoneyAmountToMinorUnits(variant.price, product.handle, variant.title);

  return {
    variantId: variant.id,
    optionValue,
    price: variant.price,
    unitPrice,
    compareAtUnitPrice: getVariantCompareAtUnitPrice(variant, product.handle),
    currency: variant.price.currencyCode,
    recipe: variant.recipe,
    displayLabelSv: getVariantLocalizedLabel(
      variant.sizeLabelSv,
      product.handle,
      variant.title,
      "custom.size_label_sv",
    ),
    displayLabelEn: getVariantLocalizedLabel(
      variant.sizeLabelEn,
      product.handle,
      variant.title,
      "custom.size_label_en",
    ),
    bestForSv: variant.bestForSv,
    bestForEn: variant.bestForEn,
    slug: buildMixSlug(variant.recipe),
  };
}

export function normalizeStickerSizeCatalog(
  product: ShopifyProduct,
): ShopifyStickerCatalog {
  const config = getShopifyConfig();
  const sizeIdByOptionValue = getStickerSizeIdByOptionValue();
  const singleEntries: ShopifyStickerSizeCatalogEntry[] = [];
  const singleBySizeId: Record<string, ShopifyStickerSizeCatalogEntry> = {};
  const mixEntries: ShopifyStickerMixCatalogEntry[] = [];

  for (const variant of product.variants.nodes) {
    const optionValue = getVariantSelectedSizeOptionValue(variant);

    if (variant.kind === "mix") {
      // Mix variants do not represent a single size. Their Shopify "Size"
      // selected option may be empty, a placeholder value, or absent entirely
      // depending on how the store is set up; we key mix entries by variantId
      // and only use the option value for display/debugging.
      mixEntries.push(createStickerMixCatalogEntry(product, variant, optionValue ?? ""));
      continue;
    }

    if (!optionValue) {
      throw new ShopifyProductContractError(
        `Shopify product "${product.handle}" variant "${variant.title}" is missing selected option "${config.stickerSizeOptionName}".`,
      );
    }

    const sizeId = sizeIdByOptionValue.get(optionValue);

    if (!sizeId) {
      continue;
    }

    if (singleBySizeId[sizeId]) {
      throw new ShopifyProductContractError(
        `Shopify product "${product.handle}" maps multiple variants to size "${sizeId}" via option "${config.stickerSizeOptionName}"="${optionValue}".`,
      );
    }

    const entry = createStickerSizeCatalogEntry(product, variant, sizeId, optionValue);

    singleEntries.push(entry);
    singleBySizeId[sizeId] = entry;
  }

  const missingSizeIds = Object.keys(config.stickerSizeOptionValues).filter(
    (sizeId) => !singleBySizeId[sizeId],
  );

  if (missingSizeIds.length) {
    throw new ShopifyProductContractError(
      `Shopify product "${product.handle}" is missing variants for configured sizes: ${missingSizeIds.join(", ")}.`,
    );
  }

  for (const mixEntry of mixEntries) {
    for (const item of mixEntry.recipe.items) {
      if (!singleBySizeId[item.sizeId]) {
        throw new ShopifyProductContractError(
          `Shopify product "${product.handle}" mix variant "${mixEntry.variantId}" references sizeId "${item.sizeId}" which has no matching single-kind entry in the same catalog.`,
        );
      }
    }
  }

  const slugsSeen = new Map<string, ShopifyStickerMixCatalogEntry>();
  for (const mixEntry of mixEntries) {
    const previous = slugsSeen.get(mixEntry.slug);
    if (previous) {
      throw new ShopifyProductContractError(
        `Shopify product "${product.handle}" mix variants produce duplicate recipe slug "${mixEntry.slug}": "${previous.displayLabelEn}" (${previous.variantId}) and "${mixEntry.displayLabelEn}" (${mixEntry.variantId}).`,
      );
    }
    slugsSeen.set(mixEntry.slug, mixEntry);
  }

  return {
    singleEntries,
    singleBySizeId,
    mixEntries,
  };
}

function normalizeStickerSizeCatalogEntry(
  product: ShopifyProduct,
  sizeId: SizeId,
): ShopifyStickerSizeCatalogEntry {
  const config = getShopifyConfig();
  const optionValue = config.stickerSizeOptionValues[sizeId];

  if (!optionValue) {
    throw new ShopifyProductContractError(
      `Sticker size "${sizeId}" is not part of the configured Shopify size contract.`,
    );
  }

  const matchingVariants = product.variants.nodes.filter(
    (variant) => getVariantSelectedSizeOptionValue(variant) === optionValue,
  );

  if (!matchingVariants.length) {
    throw new ShopifyProductContractError(
      `Shopify product "${product.handle}" has no variant matching size "${sizeId}" via option "${config.stickerSizeOptionName}"="${optionValue}".`,
    );
  }

  if (matchingVariants.length > 1) {
    throw new ShopifyProductContractError(
      `Shopify product "${product.handle}" maps multiple variants to size "${sizeId}" via option "${config.stickerSizeOptionName}"="${optionValue}".`,
    );
  }

  return createStickerSizeCatalogEntry(product, matchingVariants[0], sizeId, optionValue);
}

function assertStickerProductContract(product: ShopifyProduct): void {
  const config = getShopifyConfig();
  const sizeOption = product.options.find(
    (option) => option.name === config.stickerSizeOptionName,
  );

  if (!sizeOption) {
    throw new ShopifyProductContractError(
      `Shopify product "${product.handle}" is missing the configured size option "${config.stickerSizeOptionName}".`,
    );
  }

  const missingValues = Object.entries(config.stickerSizeOptionValues)
    .filter(([, optionValue]) => !sizeOption.values.includes(optionValue))
    .map(([sizeId, optionValue]) => `${sizeId} -> ${optionValue}`);

  if (missingValues.length) {
    throw new ShopifyProductContractError(
      `Shopify product "${product.handle}" does not match the configured size option values: ${missingValues.join(", ")}.`,
    );
  }
}

export async function getShopifyStickerProduct(options?: {
  fetch?: typeof fetch;
}): Promise<ShopifyProduct> {
  const config = getShopifyConfig();
  const data = await shopifyStorefrontRequest<
    ShopifyProductByHandleResponse,
    { handle: string }
  >({
    query: STICKER_PRODUCT_QUERY,
    operationName: "GetStickerProductByHandle",
    variables: {
      handle: config.stickerProductHandle,
    },
    fetch: options?.fetch,
  });

  if (!data.product) {
    throw new ShopifyProductNotFoundError(config.stickerProductHandle);
  }

  const product = normalizeStickerProduct(data.product);
  assertStickerProductContract(product);

  return product;
}

export async function getShopifyStickerSizeCatalog(options?: {
  fetch?: typeof fetch;
}): Promise<ShopifyStickerCatalog> {
  const product = await getShopifyStickerProduct(options);
  return normalizeStickerSizeCatalog(product);
}

export async function getShopifyStickerSizeCatalogEntry(
  sizeId: SizeId,
  options?: {
    fetch?: typeof fetch;
  },
): Promise<ShopifyStickerSizeCatalogEntry> {
  const product = await getShopifyStickerProduct(options);
  return normalizeStickerSizeCatalogEntry(product, sizeId);
}
